/**
 * Calculates average scores for each model
 * @param {Array} modelColumns - Array of model objects
 * @param {Array} benchmarkData - Benchmark data with scores
 * @returns {Object} - Object with model IDs as keys and average scores as values
 */
export const calculateAverageScores = (modelColumns, benchmarkData) => {
  const averageScores = {};
  
  modelColumns.forEach(model => {
    // Handle the ID mapping from 'hanooman' to 'asi1_mini'
    const scoreKey = model.id === 'asi1_mini' ? 'hanooman' : model.id;
    const scores = benchmarkData.map(item => item.scores[scoreKey]);
    averageScores[model.id] = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  });
  
  return averageScores;
};

/**
 * Find the best model(s) for a specific task
 * @param {Object} task - Task data object with scores
 * @param {Array} modelColumns - Array of model objects
 * @returns {Object} - Object with best score and array of best model names
 */
export const getBestModel = (task, modelColumns) => {
  let best = { score: 0, models: [] };
  
  modelColumns.forEach(model => {
    const scoreKey = model.id === 'asi1_mini' ? 'hanooman' : model.id;
    const score = task.scores[scoreKey];
    
    if (score > best.score) {
      best = { score, models: [model.name] };
    } else if (score === best.score) {
      best.models.push(model.name);
    }
  });
  
  return best;
};

/**
 * Get tasks where each model performs best
 * @param {Array} benchmarkData - Benchmark data with scores
 * @param {Array} modelColumns - Array of model objects
 * @returns {Array} - Array of model strength objects, sorted by count
 */
export const getModelStrengths = (benchmarkData, modelColumns) => {
  const strengths = {};
  
  modelColumns.forEach(model => {
    strengths[model.id] = {
      model: model.name,
      count: 0,
      tasks: []
    };
  });

  benchmarkData.forEach(task => {
    let highestScore = 0;
    let bestModels = [];
    
    modelColumns.forEach(model => {
      const scoreKey = model.id === 'asi1_mini' ? 'hanooman' : model.id;
      const score = task.scores[scoreKey];
      
      if (score > highestScore) {
        highestScore = score;
        bestModels = [model.id];
      } else if (score === highestScore) {
        bestModels.push(model.id);
      }
    });
    
    bestModels.forEach(modelId => {
      strengths[modelId].count += 1;
      const scoreKey = modelId === 'asi1_mini' ? 'hanooman' : modelId;
      strengths[modelId].tasks.push({
        name: task.task,
        score: task.scores[scoreKey]
      });
    });
  });
  
  return Object.values(strengths).sort((a, b) => b.count - a.count);
};

/**
 * Get the score key for a model ID, handling special cases
 * @param {string} modelId - The model ID
 * @returns {string} - The correct score key to use
 */
export const getScoreKey = (modelId) => {
  return modelId === 'asi1_mini' ? 'hanooman' : modelId;
};
